import * as Sentry from '@sentry/react';
import type { ReactNode } from 'react';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
} from 'react';
import { isMobile } from 'react-device-detect';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';

import { ConfigLoaded } from '@apptypes/Config';
import type UserStatus from '@apptypes/UserStatus';
import type { NET_USER, TwoFactorAuth } from '@apptypes/UserStatus';
import type RailsApiResponse from '@apptypes/api/RailsApiResponse';
import {
  ComponentName,
  ComponentSettings,
  GeolocationServices,
  LocalStorageKeys,
  TestEnv,
} from '@constants';
import useApi from '@hooks/useApi';
import { useConfig } from '@hooks/useConfig';
import useGTM from '@hooks/useGTM';
import useIdleTicker from '@hooks/useIdleTicker';
import type { RootState } from '@state/index';
import { enableModal } from '@state/reducers/modals.ts';
import {
  fetchUserBalance,
  setLogin,
  setLogout,
  setPromptSetLimits,
  setUser,
} from '@state/reducers/user';
import { gtmDatakey } from '@utils/GoogleTagManager.ts';
import { getApi, postApi } from '@utils/apiUtils';

export interface UserAuth {
  user: UserStatus;
  signin: (
    login: string,
    password: string,
    pin?: number,
  ) => Promise<{
    success: boolean;
    message: string | null;
    twoFactorAuthRequired?: boolean;
    total_deposit_count?: number;
    userActivationNeeded?: boolean;
  }>;
  signout: () => Promise<void>;
  updateUser: (forceUpdate?: boolean) => void;
}

export const AuthContext = createContext<UserAuth | null>(null);

export function useAuth(): UserAuth {
  const instance = useContext<UserAuth | null>(AuthContext);
  if (!instance) {
    throw new Error('There was an error getting auth instance from context');
  }
  return instance;
}

export type AuthProviderProps = {
  children?: ReactNode;
};

class StatusResponseError {
  notLoggedIn: boolean;

  constructor(res: RailsApiResponse<UserStatus>) {
    this.notLoggedIn = res.Code === 1;
  }
}

enum LoginStatus {
  ActivationNeeded = 9,
}

async function makeStatusCheck(statusUrl: string) {
  const statusCheck = await getApi<RailsApiResponse<UserStatus>>(statusUrl);
  if (!statusCheck.Success) {
    throw new StatusResponseError(statusCheck);
  }

  return statusCheck.Data;
}

export const AuthProvider = ({ ...props }: AuthProviderProps) => {
  const retryCount = useRef(0);
  const sendDataToGTM = useGTM();
  const { configLoaded, locale } = useConfig((prev, next) => {
    const localeEqual = prev.locale === next.locale;
    const configEqual = prev.configLoaded === next.configLoaded;
    return localeEqual && configEqual;
  });
  const dispatch = useDispatch();
  const geolocationService =
    window.__config__.componentSettings?.geolocationSettings?.service;
  const isLogoutMessageModalEnabled =
    window.__config__.componentSettings?.logout?.logoutMessageModalEnabled;
  const user = useSelector((state: RootState) => state.user);
  const { error, mutate } = useApi<UserStatus | null, StatusResponseError>(
    !TestEnv && !window.PRERENDER_CACHE ? '/restapi/v3/user/status' : null,
    url => makeStatusCheck(url),
    {
      revalidateOnFocus: !window.__config__.componentSettings?.v2Auth,
      refreshInterval: window.__config__.componentSettings?.v2Auth ? 0 : 600000, //v2 - disabled, otherwise - 10 min
      onSuccess: data => {
        retryCount.current = 0;
        let formattedUser: UserStatus = {
          logged_in: false,
          loading: false,
          needsSync: false,
          disable_logout_modal: !isLogoutMessageModalEnabled || false,
        };
        if (data?.id != null) {
          formattedUser = {
            ...formattedUser,
            ...data,
            logged_in: !!data.id,
          };
        }
        if (!shallowEqual(user, formattedUser)) {
          dispatch(setUser(formattedUser));
        }
      },
      onErrorRetry: (
        error: StatusResponseError,
        _,
        _1,
        revalidate,
        options,
      ) => {
        retryCount.current++;
        const loggedOutUser = {
          logged_in: false,
          loading: false,
          needsSync: false,
          disable_logout_modal: !isLogoutMessageModalEnabled || false,
        };
        if (error.notLoggedIn) {
          if (retryCount.current > 2 || !user.logged_in) {
            retryCount.current = 0;
            if (!shallowEqual(user, loggedOutUser))
              dispatch(setUser(loggedOutUser));
            return;
          }
          setTimeout(() => revalidate(options), 2000);
        } else {
          if (retryCount.current > 10) {
            retryCount.current = 0;
            if (!shallowEqual(user, loggedOutUser))
              dispatch(
                setUser({
                  logged_in: false,
                  loading: false,
                  needsSync: false,
                  disable_logout_modal: !isLogoutMessageModalEnabled || false,
                }),
              );
            return;
          }
          setTimeout(() => revalidate(options), 10000);
        }
      },
      isPaused: () =>
        !(user.needsSync || user.logged_in) || !!user.registration_id,
    },
  );
  useEffect(() => {
    if (!user.loading && configLoaded === ConfigLoaded.Loaded) {
      sendDataToGTM({
        [gtmDatakey('user.GUID')]: user.id,
        [gtmDatakey('user.LoginStatus')]: user.logged_in
          ? 'LoggedIn'
          : 'LoggedOut',
        [gtmDatakey('user.Platform')]: isMobile ? 'Mobile' : 'Desktop',
        [gtmDatakey('user.Language')]: locale || '',
        event: 'userStatusChange',
      });
      if (ComponentSettings?.useBalancesEndpoint && user.logged_in) {
        dispatch(fetchUserBalance());
      }
    }
  }, [user.logged_in, user.loading, configLoaded]);

  const signin = async (login: string, password: string, pin?: number) => {
    const promptSetLimits = user.prompt_set_limits ?? false;
    const res = await postApi<
      RailsApiResponse<NET_USER | TwoFactorAuth | null>
    >('/restapi/v1/user/login', {
      login,
      password,
      pin,
    }).catch((res: RailsApiResponse<null>) => res);
    const activationNeeded =
      (res?.Data as NET_USER)?.Code === LoginStatus.ActivationNeeded;
    if (res.Success && !activationNeeded) {
      if ((res.Data as TwoFactorAuth).authentication_required) {
        return {
          success: res.Success,
          message: res.Message,
          twoFactorAuthRequired: true,
        };
      }
      const data = res.Data as NET_USER;
      dispatch(setLogin(data));
      localStorage.removeItem(LocalStorageKeys.activeGame);
      sendDataToGTM({
        'tglab.user.GUID': data.PlayerId!,
        event: 'SuccessfulLogin',
      });
      setTimeout(async () => {
        await mutate();
        if (user.validator_status === 1) {
          dispatch(setPromptSetLimits(promptSetLimits));
        }
      }, 2000);
    } else if (activationNeeded) {
      const data = res.Data as NET_USER;
      dispatch(setLogin(data));
      setTimeout(async () => {
        await mutate();
        if (user.validator_status === 1) {
          dispatch(setPromptSetLimits(promptSetLimits));
        }
      }, 2000);
      return {
        success: res.Success,
        message: res.Message,
        userActivationNeeded: true,
      };
    } else {
      sendDataToGTM({
        'tglab.Error': res.Message || 'request timeout',
        event: 'LoginFailed',
      });
    }
    return {
      success: res.Success,
      message: res.Message,
      total_deposit_count: (res.Data as NET_USER)?.total_deposit_count,
    };
  };
  const signout = async () => {
    // 1. Don't swallow the logout result. A logout request that silently fails
    // (or never invalidates the server session) is exactly what leaves users
    // "logged in until they clear their browser cache", so surface it instead
    // of hiding it behind `.catch(err => err)`.
    let logoutConfirmed = false;
    try {
      const res = await getApi<RailsApiResponse<null>>(
        '/restapi/v1/user/logout',
      );
      logoutConfirmed = res?.Success !== false;
    } catch (err) {
      Sentry.captureMessage('User logout request failed', {
        level: Sentry.Severity.Error,
        tags: {
          'http.status': (err as RailsApiResponse<null>)?.Code ?? 'unknown',
        },
      });
    }

    // 2. Verify a real status invalidation before we trust the logout. A
    // rejected status call (401 / Code 1) is the expected "logged out" result;
    // a live user id means the session survived the logout and the client is
    // about to show a state that isn't true.
    try {
      const status = await getApi<RailsApiResponse<UserStatus>>(
        '/restapi/v3/user/status',
      );
      if (status?.Success && status.Data?.id != null) {
        logoutConfirmed = false;
        Sentry.captureMessage('Session still active after logout', {
          level: Sentry.Severity.Error,
        });
      } else {
        logoutConfirmed = true;
      }
    } catch {
      logoutConfirmed = true;
    }

    if (isLogoutMessageModalEnabled && !user.disable_logout_modal) {
      dispatch(enableModal(ComponentName.LogoutMessageModal));
    }
    localStorage.removeItem(LocalStorageKeys.activeGame);
    // Drop the cached status so a later revalidation/mount can't resurrect the
    // logged-in state from a stale SWR entry. If the session could not be
    // confirmed dead, revalidate against the server so the UI reflects the
    // real (still-authenticated) state rather than a false "logged out".
    await mutate(null, !logoutConfirmed);
    dispatch(setLogout());

    return;
  };
  useIdleTicker(
    !!window.__config__.componentSettings?.userIdleTimeout && user.logged_in,
    signout,
  );
  const updateUser = useCallback(
    async (forceUpdate = false) => {
      if ((!error && !retryCount.current && user.logged_in) || forceUpdate) {
        await mutate();
        return;
      }
    },
    [error, mutate, user.logged_in],
  );
  const value: UserAuth = {
    user,
    signin,
    signout,
    updateUser,
  };
  return <AuthContext.Provider value={value} {...props} />;
};
